// biome-ignore format: historic

import { useLazyQuery } from '@apollo/client/react';
import { requiresAuth } from '@modules/auth/requires-auth';
import { isIdentityBelongToAnonUser } from '@modules/auth-helpers';
import { auth } from '@modules/firebase';
import { logger } from '@modules/logger';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { GetUserProfileDetailsDocument } from './user-profile-details.graphql';
import { type UserDetails, validateApolloResponse } from './user-profile-details-validator';
import {
  getUserHomePageRoute,
  getUserProfileFullName,
  isAtLeastAdmin,
  isAtLeastPremiumUser,
  isAtLeastTeamAdmin,
} from './user-profile-helpers';

type UserProfileContextType = {
  userProfileDetails: UserDetails;
  refetchProfileDetails: () => Promise<void>;
  isRefetchingProfileDetails: boolean;
  helpers: {
    fullName: string | undefined;
    isAtLeastAdmin: boolean;
    isAtLeastTeamAdmin: boolean;
    isAtLeastPremium: boolean;
    homePageRoute: string;
  };
};

/**
 * Context storing user profile details and helpers, used by authenticated routes.
 */

// biome-ignore lint/style/noNonNullAssertion: Always present on authenticated routes
export const UserProfileContext = createContext<UserProfileContextType>(null!);

/**
 * Custom hook to access user profile context.
 * @throws Error if used outside of UserProfileProvider.
 */
export function useUserProfile<ThrowIfMissing extends boolean = true>(
  throwIfMissing?: ThrowIfMissing,
): ThrowIfMissing extends true ? UserProfileContextType : UserProfileContextType | null {
  const userProfile = useContext(UserProfileContext);

  if (!userProfile && throwIfMissing !== false) {
    logger.error('useUserProfile must be used on authenticted routes', {
      module: 'user-profile',
    });
    if (process.env.NODE_ENV === 'development') {
      throw new Error('useUserProfile must be used on a page with the UserProfileProvider wrapper');
    }
  }

  // If throwIfMissing is true, force non-null type, otherwise allow null
  return userProfile as unknown as ThrowIfMissing extends true
    ? UserProfileContextType
    : UserProfileContextType | null;
}

/**
 * Fetch state union type for user profile.
 */
type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: UserDetails }
  | { status: 'error'; error: Error };

/**
 * Provides user profile details to children components on authenticated routes.
 */
export const UserProfileProvider = ({
  children,
}: {
  children: ((userProfileDetails: UserDetails | null) => ReactNode) | ReactNode;
}) => {
  const { pathname, push, query, isReady, asPath } = useRouter();

  const [getProfileDetails] = useLazyQuery(GetUserProfileDetailsDocument, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const [isRefetching, setIsRefetching] = useState(false);

  const isAuthRoute = requiresAuth({ pathname, hasWorkspace: !!query.workspace });

  const [fetchState, setFetchState] = useState<FetchState>({
    status: 'idle',
  });

  const fetchUserProfile = useCallback(async () => {
    // This gate only applies to auth routes. Without this guard, a bounce from an auth route to a
    // non-auth route (e.g. /login) re-fires this callback — it depends on asPath — while fetchState
    // is still 'loading', so it redirects again using the /login asPath, nesting /login into the
    // `goto` param and looping the user on the login page.
    if (!isAuthRoute) {
      return;
    }

    // Preserve the current location so the user returns here after logging in (deep-linked auth
    // routes like /oauth/authorize otherwise lose their query params, e.g. the OAuth request).
    const loginHref = `/login?goto=${encodeURIComponent(asPath)}`;

    // Wait for Firebase to resolve auth state before checking.
    // Without this, auth.currentUser can be null during init and passing incorrect auth state to isIdentityBelongToAnonUser.
    await auth.authStateReady();
    const isAnonUser = isIdentityBelongToAnonUser(auth.currentUser);

    if (isAnonUser) {
      push(loginHref);
      return;
    }

    const { data, error } = await getProfileDetails();

    if (error?.message === 'INVALID_TOKEN') {
      push(loginHref);
      return;
    }

    if (error || !data) {
      logger.error('Failed to fetch user profile details', {
        module: 'user-profile',
        error,
      });
      setFetchState({ status: 'error', error: new Error('Something went wrong') });
      return;
    }

    const userProfileDetails = validateApolloResponse(data.user_details);
    setFetchState({ status: 'success', data: userProfileDetails });
    setIsRefetching(false);
  }, [push, getProfileDetails, asPath, isAuthRoute]);

  // // Only fetch if the auth route changes to auth
  useEffect(() => {
    if (!isReady) return;

    if (isAuthRoute) {
      setFetchState({ status: 'loading' });
    } else {
      setFetchState({ status: 'idle' });
    }
  }, [isAuthRoute, isReady]);

  useEffect(() => {
    if (fetchState.status === 'loading') {
      fetchUserProfile();
    }
  }, [fetchState.status, fetchUserProfile]);

  const refetchProfileDetails = useCallback(async () => {
    setIsRefetching(true);
    await fetchUserProfile();
  }, [fetchUserProfile]);

  // Wait for router hydration before rendering anything.
  // For non-auth routes (e.g. login), render children immediately so SSG
  // pre-rendered HTML is not replaced with an empty tree during hydration.
  if (!isReady) {
    if (!isAuthRoute) {
      return <>{typeof children === 'function' ? children(null) : children}</>;
    }
    return null;
  }

  // Render children if no auth is required.
  if (!isAuthRoute) {
    return <>{typeof children === 'function' ? children(null) : children}</>;
  }

  // If the user is not authenticated, we need to fetch the user details from the cache
  if (fetchState.status === 'error') {
    return <div>Something went wrong</div>;
  }

  // Only shown once the user transitions from unauthenticated to authenticated
  if (fetchState.status === 'loading' || fetchState.status === 'idle') {
    return null;
  }

  // Success or refetching
  const userProfileDetailsData = fetchState.data;
  const currentWorkspaceUrlId = typeof query.workspace === 'string' ? query.workspace : undefined;

  if (
    currentWorkspaceUrlId &&
    currentWorkspaceUrlId !== userProfileDetailsData.currentWorkspace.id
  ) {
    const correctedPath = asPath.replace(
      `/${currentWorkspaceUrlId}`,
      `/${userProfileDetailsData.currentWorkspace.id}`,
    );

    logger.warn('User was directed to incorrect workspace, redirecting to correct workspace', {
      module: 'user-profile',
      user_id: userProfileDetailsData.id,
      incorrect_workspace_id: currentWorkspaceUrlId,
      correct_workspace_id: userProfileDetailsData.currentWorkspace.id,
    });

    push(correctedPath);
    return null;
  }

  return (
    <UserProfileContext.Provider
      value={{
        userProfileDetails: userProfileDetailsData,
        helpers: {
          isAtLeastAdmin: isAtLeastAdmin(userProfileDetailsData),
          isAtLeastTeamAdmin: isAtLeastTeamAdmin(userProfileDetailsData),
          isAtLeastPremium: isAtLeastPremiumUser(userProfileDetailsData),
          fullName: getUserProfileFullName(userProfileDetailsData),
          homePageRoute: getUserHomePageRoute(userProfileDetailsData),
        },
        refetchProfileDetails,
        isRefetchingProfileDetails: isRefetching,
      }}
    >
      {typeof children === 'function' ? children(userProfileDetailsData) : children}
    </UserProfileContext.Provider>
  );
};
